<template>
  <div class="c-editor-menu border-rounded-b-2 mt-[10px] overflow-x-auto" :style="style">
    <div class="c-editor-menu__content px-3 py-1 gap-1 flex items-center">
      <div class="flex flex-wrap">
        <div
          v-if="isTextSelected || (!isAddingTable && !editor.isActive('table'))"
          class="flex items-center justify-between gap-1"
        >
          <div v-if="allowEdit" class="flex items-center">
            <CTextTypeSelect :editor="editor" />

            <template v-if="showFontControls">
              <CFontDropdown
                v-if="!editorFeatures.canvasTokens"
                :editor="editor"
                :themeOverrides="{ active: activeButtonThemeOverrides, default: defaultButtonThemeOverrides }"
              />

              <CFontSizeSelect
                :editor="editor"
                :renderLabel="renderAutoCompleteOption"
                :value="currentStyling.fontSize"
              />

              <CColorPicker
                class="c-editor-menu__color-picker px-4 w-auto"
                :renderLabel="renderColorLabel"
                :swatches="palette"
                :value="editor.getAttributes('textStyle').color || currentStyling.color"
                @update:value="editor.chain().focus().setColor($event).run()"
              />
            </template>
          </div>
        </div>

        <div v-if="isTextSelected || (!isAddingTable && !editor.isActive('table'))" class="flex gap-1">
          <CButton
            :class="{ 'c-button--active': editor.isActive('bold') }"
            :themeOverrides="editor.isActive('bold') ? activeButtonThemeOverrides : defaultButtonThemeOverrides"
            @click="editor.chain().focus().toggleBold().run()"
          >
            <CIcon faType="fa" icon="bold" :size="16" />
          </CButton>

          <CButton
            :themeOverrides="editor.isActive('italic') ? activeButtonThemeOverrides : defaultButtonThemeOverrides"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <CIcon faType="fa" icon="italic" :size="16" />
          </CButton>

          <CButton
            :themeOverrides="editor.isActive('underline') ? activeButtonThemeOverrides : defaultButtonThemeOverrides"
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <CIcon faType="fa" icon="underline" :size="16" />
          </CButton>

          <CButton
            :themeOverrides="editor.isActive('strike') ? activeButtonThemeOverrides : defaultButtonThemeOverrides"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <CIcon faType="fa" icon="strikethrough" :size="16" />
          </CButton>
        </div>
        <div v-if="isTextSelected || (!isAddingTable && !editor.isActive('table'))" class="flex items-center gap-1">
          <CDivider vertical />
          <CAlignmentSelect :editor="editor" :renderLabel="renderOption" />
          <CListSelect :editor="editor" :renderLabel="renderOption" />
          <CLineHeightSelect
            v-if="showFontControls"
            :editor="editor"
            :renderLabel="(option) => renderOption(option, { hideOptionIcon: true })"
          />
          <CDivider vertical />

          <CInsertDropdown
            :editor="editor"
            :themeOverrides="{ active: activeButtonThemeOverrides, default: defaultButtonThemeOverrides }"
            @on-content-link-click="isAddingContentLink = !isAddingContentLink"
            @on-link-click="isAddingLink = !isAddingLink"
          />
          <CDynamicValueTreeSelect
            :data="crmShape"
            :themeOverrides="{ active: activeButtonThemeOverrides, default: defaultButtonThemeOverrides }"
            @select="appendDynamicValue"
          />
          <CHandlebarsHelperTreeSelect
            v-if="!!launchDarkly.enable_handlebar_template_support"
            :themeOverrides="{ active: activeButtonThemeOverrides, default: defaultButtonThemeOverrides }"
            @select="appendDynamicValue"
          />
          <CFormatterWithFieldSelect
            v-if="!!launchDarkly.enable_handlebar_template_support"
            :crmData="crmShape"
            :themeOverrides="{ active: activeButtonThemeOverrides, default: defaultButtonThemeOverrides }"
            @select="appendDynamicValue"
          />
          <CAiRichTextActions
            :editor="editor"
            :themeOverrides="{ active: activeButtonThemeOverrides, default: defaultButtonThemeOverrides }"
            @end-loading="emit('endLoading')"
            @start-loading="emit('startLoading')"
          />
        </div>

        <CTableToolbar
          v-if="isAddingTable || editor.isActive('table')"
          :editor="editor"
          :theme-overrides="{ active: activeButtonThemeOverrides, default: defaultButtonThemeOverrides }"
        />
      </div>
    </div>
    <CContentLinkToolbar
      v-if="isAddingContentLink || editor.isActive('contentLink')"
      class="px-4 mb-3"
      :editor="editor"
      @close="isAddingContentLink = false"
      @openFile="(payload) => emit('openFile', payload)"
    />
    <CHyperLinkToolbar
      v-if="isAddingLink || editor.isActive('link')"
      class="px-4 mb-3"
      :editor="editor"
      @close="isAddingLink = false"
    />
  </div>
</template>

<script lang="ts">
import type { SelectBaseOption } from 'naive-ui/lib/select/src/interface'
import { DEFAULT_FONT_SIZE } from '../extensions/paragraphExtended'

export interface Option extends SelectBaseOption {
  label: string
  icon: string
  value: string
}

export interface DropdownOption {
  label: string
  icon: string
  key: string
}

interface Props {
  editor: Editor
  allowEdit?: boolean
  style?: Record<string, any>
}
</script>

<script setup lang="ts">
import { computed, h, inject, ref, type ComputedRef, type VNode } from 'vue'
import type { Editor } from '@tiptap/vue-3'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CDivider from '@lib/components/CDivider/CDivider.vue'
import CTextTypeSelect from '@lib/components/CRichTextEditor/components/CTextTypeSelect.vue'
import CFontDropdown from '@lib/components/CRichTextEditor/components/CFontDropdown.vue'
import CFontSizeSelect from '@lib/components/CRichTextEditor/components/CFontSizeSelect.vue'
import CHyperLinkToolbar from '@lib/components/CRichTextEditor/components/CHyperLinkToolbar.vue'
import CContentLinkToolbar from '@lib/components/CRichTextEditor/components/CContentLinkToolbar.vue'
import CInsertDropdown from '@lib/components/CRichTextEditor/components/CInsertDropdown.vue'
import CLineHeightSelect from '@lib/components/CRichTextEditor/components/CLineHeightSelect.vue'
import CListSelect from '@lib/components/CRichTextEditor/components/CListSelect.vue'
import CColorPicker from '@lib/components/CColorPicker/CColorPicker.vue'
import CAlignmentSelect from '@lib/components/CRichTextEditor/components/CAlignmentSelect.vue'
import CTableToolbar from '@lib/components/CRichTextEditor/components/CTableToolbar.vue'
import CAiRichTextActions from '@lib/components/CRichTextEditor/components/CAiRichTextActions.vue'
import CDynamicValueTreeSelect from '@lib/components/CRichTextEditor/components/CDynamicValueTreeSelect.vue'
import CHandlebarsHelperTreeSelect from '@lib/components/CRichTextEditor/components/CHandlebarsHelperTreeSelect.vue'
import CFormatterWithFieldSelect from '@lib/components/CRichTextEditor/components/CFormatterWithFieldSelect.vue'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import useCanvas from '@lib/apps/canvas-builder/composables/useCanvas'
import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import useThemeVars from '@lib/composables/useThemeVars'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
const props = defineProps<Props>()
const emit = defineEmits<{
  startLoading: []
  endLoading: []
  openFile: [{ fileId: string; pageIndex?: number }]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const isAddingLink = ref(false)
const isAddingContentLink = ref(false)
const isAddingTable = ref(false)

const { areTokenConstraintsActive } = useCanvas()
const { palette } = useCanvasTheme()
const { crmShape } = useCrmShape()
const themeVars = useThemeVars()

const currentStyling = computed(() => {
  if (!props.editor || typeof window === 'undefined') {
    return {
      color: themeVars.value.text2,
      fontSize: DEFAULT_FONT_SIZE.toString(),
    }
  }

  const { from, to, empty } = props.editor.state.selection

  // Initialize default values
  let color = themeVars.value.text2
  let fontSize = DEFAULT_FONT_SIZE.toString()

  // Get text style attributes directly from the editor
  const textStyleAttrs = props.editor.getAttributes('textStyle')

  // Set color and fontSize if present in textStyleAttrs
  if (textStyleAttrs.color) {
    color = textStyleAttrs.color
  }

  if (textStyleAttrs.fontSize) {
    fontSize = textStyleAttrs.fontSize
  }

  // If both attributes are set from textStyleAttrs, we can return early
  if (textStyleAttrs.color && textStyleAttrs.fontSize) {
    return { color, fontSize }
  }

  // If selection is empty and cursor is at the end of document
  if (empty && from === to && from === props.editor.state.doc.content.size) {
    // Look back to the previous position for attributes
    const prevPos = Math.max(0, from - 1)
    const $prevPos = props.editor.state.doc.resolve(prevPos)

    // Try to get attributes from previous node
    if ($prevPos.parent) {
      const prevNodeAttrs = $prevPos.parent.attrs
      if (prevNodeAttrs.fontSize && !textStyleAttrs.fontSize) {
        fontSize = prevNodeAttrs.fontSize.toString()
      }
      if (prevNodeAttrs.color && !textStyleAttrs.color) {
        color = prevNodeAttrs.color
      }
    }
  }

  // Fall back to DOM-based style detection for any attributes not yet set
  const currentNode = props.editor.view.domAtPos(from)

  if (!currentNode?.node) {
    return { color, fontSize }
  }

  // Get the actual text node or its closest parent element
  const textNode =
    currentNode.node.nodeType === Node.TEXT_NODE
      ? currentNode.node.parentElement
      : currentNode.node instanceof Element
        ? currentNode.node
        : null

  if (!textNode) {
    return { color, fontSize }
  }

  // Standard case - use the computed style from the found node
  const computedStyle = window.getComputedStyle(textNode)

  // Only update values that haven't been set by higher priority methods
  if (!textStyleAttrs.color) {
    color = computedStyle.color
  }

  if (!textStyleAttrs.fontSize) {
    fontSize = computedStyle.fontSize.replace(/[^0-9.]/g, '')
  }

  return { color, fontSize }
})

const editorFeatures = computed(() => ({
  canvasTokens: !!launchDarkly.value.enable_canvas_tokens && areTokenConstraintsActive.value,
  userFontControl: !!launchDarkly.value.allow_user_to_edit_font_size_and_color_in_canvas_tokens,
}))

const showFontControls = computed(() => !editorFeatures.value.canvasTokens || editorFeatures.value.userFontControl)

const activeButtonThemeOverrides = {
  color: `${themeVars.value.primary}2B`,
  colorHover: `${themeVars.value.primary}2B`,
  paddingMedium: '0px 8px',
}

const defaultButtonThemeOverrides = {
  color: 'transparent',
  colorHover: 'transparent',
  paddingMedium: '0px 8px',
  colorDisabled: 'transparent',
}

const appendDynamicValue = (value: string) => {
  props.editor.chain().focus().insertContent(`${value} `).run()
}

const isTextSelected = computed(() => {
  const { from, to } = props.editor.state.selection
  return from !== to
})

const renderAutoCompleteOption: (option: Option) => VNode = (option: Option) =>
  h('span', { class: 'font-500' }, option.label)

const renderOption: (option: Option, options?: { hideOptionIcon?: boolean }) => VNode = (
  option: Option,
  options?: { hideOptionIcon?: boolean },
) => {
  const iconClass = {
    'flex items-center gap-2 uppercase font-500': true,
    'n-base-select-option--hide-option-icon': options?.hideOptionIcon,
  }
  return h('div', { class: iconClass }, [
    h(CIcon, {
      icon: option.icon,
      faType: 'fa',
      size: 16,
    }),
    h('span', { class: 'c-editor-menu__select-label' }, option.label),
  ])
}

const renderColorLabel = (color: string | null) =>
  h(CIcon, {
    faType: 'fa',
    icon: 'circle',
    color: color ?? undefined,
    size: 16,
  })
</script>

<style lang="scss" scoped>
.c-editor-menu {
  font-family: 'Roboto', sans-serif;
  box-shadow: 0px 6px 8px 0px rgba(41, 51, 92, 0.13);
  border: 1px solid v-bind('themeVars.primary5');
  background: v-bind('themeVars.primary6');
}

.c-editor-menu :deep(.n-base-selection .n-base-suffix) {
  right: 6px;
}

.c-editor-menu :deep(.n-divider.n-divider--vertical) {
  margin: 0;
}

.c-editor-menu :deep(.n-button:not(.n-button--disabled)):active {
  background-color: transparent;
}

.c-editor-menu :deep(.n-base-selection .n-base-selection-label .n-base-selection-input) {
  padding: 6px !important;
  padding-right: 22px !important;
}

.c-editor-menu :deep(.n-base-selection .n-base-suffix .n-base-suffix__arrow) {
  font-size: 12px !important;
}

.c-editor-menu__content {
  :deep(.n-base-selection.n-base-selection--selected) {
    text-transform: uppercase;
    font-weight: 500;
  }

  :deep(.n-base-selection-input__content) {
    .c-editor-menu__select-label {
      display: none;
    }
  }
}

.c-editor-menu__color-picker {
  :deep(.n-color-picker-trigger) {
    border: none;

    .n-color-picker-trigger__value {
      display: flex;
      align-items: center;
      border: 1px solid v-bind('themeVars.primary4');
      border-radius: 50%;
    }

    .n-color-picker-checkboard {
      &,
      & + div {
        display: none;
      }
    }
  }
}
</style>
